export type BreakdownGroupBy = "product" | "sku";

interface BreakdownConfig {
  label: string;
  legendSuffix: string;
  otherLabelSuffix: string;
  infoContent: string;
}

export const BREAKDOWN_CONFIG: Record<BreakdownGroupBy, BreakdownConfig> = {
  product: {
    label: "Product",
    legendSuffix: "products",
    otherLabelSuffix: "",
    infoContent:
      "This waffle chart shows cost distribution across Databricks products. Each colored cell represents 1% of total cost. Hover over cells to see the product name and percentage. The table below shows detailed cost breakdown with DBUs, cost, and percentage for each product.",
  },
  sku: {
    label: "SKU",
    legendSuffix: "SKU types",
    otherLabelSuffix: " SKUs",
    infoContent:
      "This waffle chart shows cost distribution across SKU types (pricing tiers). Each colored cell represents 1% of total cost. Hover over cells to see the SKU name and percentage. The table below shows detailed cost breakdown with DBUs, cost, and percentage for each SKU.",
  },
};

export const COMPUTE_EFFICIENCY_COMPUTE_TYPE_COLORS: Record<string, string> = {
  jobs: "#4CAF50",
  dlt: "#8BC34A",
  sql: "#9C27B0",
  all_purpose: "#F44336",
  model_serving: "#FF9800",
  other: "#9E9E9E",
};

export const COMPUTE_EFFICIENCY_DEPLOYMENT_TYPE_COLORS: Record<string, string> = {
  serverless: "#4CAF50",
  provisioned: "#FF9800",
};

export const COMPUTE_EFFICIENCY_ENGINE_TYPE_COLORS: Record<string, string> = {
  standard: "#4CAF50",
  photon: "#FF9800",
};

export const COMPUTE_EFFICIENCY_SCORE_COLORS: Record<string, string> = {
  healthy: "#4CAF50",
  moderate: "#FF9800",
  unhealthy: "#F44336",
  default: "#9E9E9E",
};

export const COMPUTE_EFFICIENCY_SCORE_LABELS: Record<string, string> = {
  healthy: "Healthy",
  moderate: "Moderate",
  unhealthy: "Needs Attention",
  default: "Unknown",
};

/**
 * SKU color patterns for stacked bar charts.
 * Uses regex to match SKU names and assign consistent colors.
 */
export const SKU_COLOR_PATTERNS: Array<{ pattern: RegExp; color: string }> = [
  { pattern: /JOBS.*COMPUTE.*PHOTON/i, color: "#E74C3C" },
  { pattern: /JOBS.*COMPUTE/i, color: "#FF754C" },
  { pattern: /JOBS.*SERVERLESS/i, color: "#4C9AFF" },
  { pattern: /ALL_PURPOSE.*PHOTON/i, color: "#2ECC71" },
  { pattern: /ALL_PURPOSE/i, color: "#36B37E" },
  { pattern: /INTERACTIVE/i, color: "#9B59B6" },
  { pattern: /DLT.*ADVANCED/i, color: "#F39C12" },
  { pattern: /DLT.*CORE/i, color: "#F1C40F" },
  { pattern: /DLT/i, color: "#D4AC0D" },
  { pattern: /INFERENCE/i, color: "#3498DB" },
  { pattern: /SERVERLESS/i, color: "#1ABC9C" },
];

export const SKU_DEFAULT_COLOR = "#94A3B8";

/**
 * Fallback color palette for SKUs that don't match any pattern.
 */
export const SKU_FALLBACK_COLORS = [
  "#E67E22",
  "#8E44AD",
  "#16A085",
  "#D35400",
  "#2980B9",
  "#C0392B",
  "#27AE60",
  "#7F8C8D",
  "#BDC3C7",
  "#34495E",
];

const skuColorCache = new Map<string, string>();

/**
 * Gets the color for a SKU name using pattern matching.
 * Caches results for performance.
 */
export const getSkuColor = (sku: string, index: number): string => {
  if (skuColorCache.has(sku)) {
    return skuColorCache.get(sku)!;
  }

  for (const { pattern, color } of SKU_COLOR_PATTERNS) {
    if (pattern.test(sku)) {
      skuColorCache.set(sku, color);
      return color;
    }
  }

  const color = SKU_FALLBACK_COLORS[index % SKU_FALLBACK_COLORS.length] || SKU_DEFAULT_COLOR;
  skuColorCache.set(sku, color);
  return color;
};

/**
 * Cleans up SKU names for display by removing region suffixes and PREMIUM_ prefix.
 */
export const cleanSkuName = (sku: string): string =>
  sku.replace(/^PREMIUM_|_(?:US_EAST|US_WEST|EU_WEST).*$/gi, "");

/**
 * Granularity options for charts that aggregate via backend API.
 * Values are sent as query params (e.g., granularity=day).
 */
export const CHART_PERIOD_GRANULARITY_OPTIONS = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
];

export type ChartGranularity = "day" | "week" | "month";

/**
 * SQL Warehouse SKU color patterns for warehouse cost charts.
 * Separate from the general SKU_COLOR_PATTERNS because warehouses use
 * different colors (e.g., JOBS_SERVERLESS is gray here vs blue in general SKU).
 */
export const SQL_WAREHOUSE_SKU_COLOR_PATTERNS: Array<{
  pattern: RegExp;
  color: string;
  label: string;
}> = [
  { pattern: /SERVERLESS.*SQL/i, color: "#FF754C", label: "Serverless SQL" },
  { pattern: /SQL.*PRO/i, color: "#4C9AFF", label: "SQL Pro" },
  { pattern: /JOBS.*SERVERLESS/i, color: "#6B7280", label: "Jobs Serverless" },
  { pattern: /SQL.*COMPUTE/i, color: "#36B37E", label: "SQL Compute" },
  { pattern: /SQL.*STANDARD/i, color: "#9B59B6", label: "SQL Standard" },
];

export const SQL_WAREHOUSE_SKU_DEFAULT_COLOR = "#94A3B8";

/**
 * Get color for a SQL warehouse SKU name based on pattern matching.
 */
export const getSqlWarehouseSkuColor = (sku: string): string => {
  for (const { pattern, color } of SQL_WAREHOUSE_SKU_COLOR_PATTERNS) {
    if (pattern.test(sku)) return color;
  }
  return SQL_WAREHOUSE_SKU_DEFAULT_COLOR;
};

/**
 * Get label for a SQL warehouse SKU name, shortening long names.
 */
export const getSqlWarehouseSkuLabel = (sku: string): string => {
  for (const { pattern, label } of SQL_WAREHOUSE_SKU_COLOR_PATTERNS) {
    if (pattern.test(sku)) return label;
  }
  return sku
    .replace(/^PREMIUM_/, "")
    .replace(/_US_EAST_N_VIRGINIA$/i, "")
    .replace(/_/g, " ");
};
